import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Upload, Trash2, FileUp, X, Download } from 'lucide-react';
import supabase from '../services/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../services/apiService';
import '../styles/FileManagement.css';
import { Modal } from '@mui/material';
import { invalidateCache } from '../services/cdnService';
import { Upload as S3Upload } from '@aws-sdk/lib-storage';
import { getS3Client, refreshS3Client } from '../services/s3Client';
import { Pencil } from 'lucide-react';
import StatusBadge from './shared/StatusBadge';
import { toast } from 'react-hot-toast';
import { useUsers } from '../hooks/useUsers';
import FileSidePanel from './FileSidePanel';
import JSZip from 'jszip';

const STORAGE_BUCKET = process.env.REACT_APP_SUPABASE_STORAGE_BUCKET;

const SUPPORTED_FILE_TYPES = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/tiff': ['.tiff'],
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/rtf': ['.rtf'],
  'text/plain': ['.txt'],
  'text/csv': ['.csv'],
  'text/tab-separated-values': ['.tsv'],
  'message/rfc822': ['.eml']
};

const MAX_FILE_SIZE = 2000 * 1024 * 1024; // 200MB in bytes

const MULTIPART_UPLOAD_THRESHOLD = 100 * 1024 * 1024; // 100MB threshold for multipart

const statusColors = {
  '-1': 'bg-red-100 text-red-800',    // error
  0: 'bg-gray-100 text-gray-800',      // draft
  1: 'bg-purple-100 text-purple-800',   // ai_review
  2: 'bg-blue-100 text-blue-800',      // human_review
  3: 'bg-yellow-100 text-yellow-800',   // qa
  4: 'bg-green-100 text-green-800',    // finished
  5: 'bg-red-100 text-red-800',        // returned
  6: 'bg-gray-100 text-gray-800'       // archived
};

const statusNames = {
  '-1': 'Error',
  0: 'Draft',
  1: 'AI Review',
  2: 'Human Review',
  3: 'QA',
  4: 'Finished',
  5: 'Returned',
  6: 'Archived'
};

const FileManagement = ({ onCaseNameUpdate }) => {
  const api = useAPI();
  const { user, sessionStatus, firmContext, getFirmRoleData, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { caseId } = useParams();

  const [caseName, setCaseName] = useState('');
  const [examDate, setExamDate] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isPolling, setIsPolling] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [updateModal, setUpdateModal] = useState({
    isOpen: false,
    existingFile: null,
    newFile: null
  });

  const [allCaseFiles, setAllCaseFiles] = useState([]);

  const [nameConfidence, setNameConfidence] = useState({
    value: null,
    confidence: 0,
    source: null
  });

  const [hasProducerRole, setHasProducerRole] = useState(false);

  const [caseStatus, setCaseStatus] = useState(0); // Default to draft
  const [assignedTo, setAssignedTo] = useState('');
  const [userSettings, setUserSettings] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [isProfessional, setIsProfessional] = useState(false);

  const [isEditingName, setIsEditingName] = useState(false);
  const [isRushRequest, setIsRushRequest] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { users, loading: usersLoading, error: usersError } = useUsers();

  const [physicians, setPhysicians] = useState([]);
  const [loadingPhysicians, setLoadingPhysicians] = useState(true);

  const [fileMapping, setFileMapping] = useState({});

  const [showRightPanel, setShowRightPanel] = useState(false);
  const [rightPanelWidth, setRightPanelWidth] = useState(500);
  const [currentModalFileName, setCurrentModalFileName] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [pageNumber, setPageNumber] = useState(1);

  const [showPhysicianError, setShowPhysicianError] = useState(false);

  const handlePreview = async (fileId, bucketUrl, pageNumber = 0) => {
    try {
      const fileName = fileMapping[fileId] || bucketUrl.split('/').pop();
      const bucketName = process.env.REACT_APP_SUPABASE_STORAGE_BUCKET;
      const { data: signedUrl } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(bucketUrl, 3600);

      if (!signedUrl?.signedUrl) {
        throw new Error('Failed to generate preview URL');
      }

      setCurrentModalFileName(fileName);
      setFileUrl(signedUrl.signedUrl);
      setPageNumber(pageNumber + 1);
      setShowRightPanel(true);
    } catch (error) {
      console.error('Error opening file:', error);
      toast.error('Failed to open file');
    }
  };

  const handleResize = (event, { size }) => {
    setRightPanelWidth(size.width);
  };

  const renderRightPanel = () => {
    return (
      <FileSidePanel
        isOpen={showRightPanel}
        onClose={() => setShowRightPanel(false)}
        fileName={currentModalFileName}
        fileUrl={fileUrl}
        initialPage={pageNumber - 1}
        initialWidth={rightPanelWidth}
        minWidth={300}
        maxWidth={800}
      />
    );
  };

  const renderFiles = () => {
    return Object.entries(groupFilesByCategory()).map(([categoryId, category]) => (
      <div key={categoryId} className={`rounded p-4 mb-4 ${
        category.files.some(file => file.processing_status === 0) 
          ? 'bg-gray-200 processing-background' 
          : category.files.some(file => file.processing_status === 2)
            ? 'bg-red-50'
            : 'bg-gray-100'
      }`}>
        <h3 className={`font-bold mb-2 ${
          category.files.some(file => file.processing_status === 2)
            ? 'text-red-600'
            : ''
        }`}>{category.categoryName}</h3>
        {category.files.map((file) => (
          <div key={file.file_id} className="mb-1 flex justify-between items-center">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                const pageNumber = file.page_numbers && file.page_numbers.length > 0 
                  ? file.page_numbers[0] - 1  // Convert to 0-based index
                  : 0;
                handlePreview(file.file_id, file.bucket_url, pageNumber);
              }}
              className={`hover:underline ${
                file.processing_status === 2
                  ? 'text-red-600 hover:text-red-800'
                  : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              {file.bucket_url.split('/').pop()}
            </a>
            <div className="flex items-center gap-2">
              <button
                onClick={() => downloadFile(file.bucket_url)}
                className={`${
                  file.processing_status === 2
                    ? 'text-red-400 hover:text-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Download size={18} />
              </button>
              <button
                onClick={() => deleteFile(file.file_id)}
                className={`${
                  file.processing_status === 2
                    ? 'text-red-400 hover:text-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>
    ));
  };

  useEffect(() => {
    if (sessionStatus === 'expired') {
      navigate('/login');
    } else if (sessionStatus === 'error') {
      toast.error('Having trouble connecting to the server. Will retry shortly...');
    }
  }, [sessionStatus, navigate]);

  useEffect(() => {
    const fetchPhysicians = async () => {
      try {
        const { data } = await api.get('/physicians');
        setPhysicians(data || []);
      } catch (error) {
        console.error('Error fetching physicians:', error);
        toast.error('Failed to load physicians');
      } finally {
        setLoadingPhysicians(false);
      }
    };

    fetchPhysicians();
  }, []);

  useEffect(() => {
    const setProfessionalName = async () => {
      if (!user || !firmContext?.firmId) return;

      console.log('Checking professional role with firmContext:', firmContext);
      
      if (firmContext.roleName === 'professional') {
        console.log('User is a professional, setting professional data');
        setIsProfessional(true);
        setUserSettings({ name: user.email });
        setSelectedProfessional({
          user_id: user.id,
          name: user.email
        });
      }
    };

    if (firmContext?.firmId) {
      setProfessionalName();
    }
  }, [user, firmContext]);

  useEffect(() => {
    const checkProducerRole = async () => {
      if (!firmContext?.firmId) return;

      const firmRoleData = await getFirmRoleData(firmContext.firmId);
      if (!firmRoleData) return;

      const producerRoles = ['admin', 'manager', 'analyst', 'super_admin'];
      setHasProducerRole(producerRoles.includes(firmRoleData.roleName));
    };

    if (firmContext?.firmId) {
      checkProducerRole();
    }
  }, [firmContext, getFirmRoleData]);

  useEffect(() => {
    const fetchData = async () => {
      if (user && caseId) {
        await fetchCaseDetails();
        fetchCaseFiles();
        fetchCategories();
        updateFileMapping(caseId);
      }
    };

    fetchData();
  }, [caseId, user]);

  useEffect(() => {
    const handlePatientNameUpdate = async () => {
      if (caseName === "New Case" && nameConfidence.confidence > 0.8) {
        const shouldUpdate = await new Promise(resolve => {
          setUpdateModal({
            isOpen: true,
            type: 'patient_name',
            message: `Update patient name to "${nameConfidence.value}"? (Found in ${nameConfidence.source})`,
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });

        if (shouldUpdate) {
          setCaseName(nameConfidence.value);
          await updateCaseDetails('case_name', nameConfidence.value);
        }
      }
    };

    handlePatientNameUpdate();
  }, [nameConfidence]);

  const fetchCaseDetails = async () => {
    if (caseId) {
      const { data: caseData, error: caseError } = await supabase
        .from('cases')
        .select(`
          case_name, 
          exam_dt,
          status_id,
          case_analysts (
            user_id,
            public_users (
              name
            )
          ),
          case_pros (
            user_id,
            public_users (
              name,
              user_id
            )
          )
        `)
        .eq('case_id', caseId)
        .single();

      if (caseError) {
        console.error('Error fetching case details:', caseError);
      } else if (caseData) {
        setCaseName(caseData.case_name || "New Case");
        setExamDate(caseData.exam_dt || '');
        setCaseStatus(caseData.status_id || 0);
        
        // Handle professional data
        if (caseData.case_pros) {
          const pro = caseData.case_pros;
          setSelectedProfessional({
            user_id: pro.user_id,
            name: pro.public_users?.name
          });
        }

        // Get firm settings from cache if needed
        if (user?.firm_id) {
          const firmRoleData = await getFirmRoleData(user.firm_id);
          if (firmRoleData?.firmSettings) {
            setIsRushRequest(firmRoleData.firmSettings.rush_enabled || false);
          }
        }
      }
    }
  };

  const fetchCaseFiles = async () => {
    // Fetch ALL files, including deleted ones
    const { data, error } = await supabase
      .from('case_files')
      .select(`
        file_id, 
        bucket_url,
        filename,
        category_id,
        is_deleted,
        case_file_categories (
          category_id,
          category_name
        )
      `)
      .eq('case_id', caseId);

    if (error) {
      console.error('Error fetching case files:', error);
    } else {
      // Only show non-deleted files in the UI
      setUploadedFiles(data.filter(file => !file.is_deleted));
      // Store all files (including deleted) in a separate state
      setAllCaseFiles(data);
    }
  };

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('case_file_categories')
      .select('category_id, category_name');
    
    if (error) console.error('Error fetching categories:', error);
    else setCategories(data);
  };

  const updateCaseDetails = async (field, value) => {
    const { error } = await supabase
      .from('cases')
      .update({ [field]: value })
      .eq('case_id', caseId);

    if (error) {
      console.error(`Error updating case ${field}:`, error);
    }
  };

  const handleCaseNameBlur = async (e) => {
    const newCaseName = e.target.value;
    const { error } = await supabase
      .from('cases')
      .update({ case_name: newCaseName })
      .eq('case_id', caseId);

    if (error) {
      console.error(`Error updating case name:`, error);
    } else {
      // Make sure onCaseNameUpdate exists before calling it
      if (onCaseNameUpdate) {
        onCaseNameUpdate(caseId, newCaseName);
      }
      // Also update local state
      setCaseName(newCaseName);
    }
  };

  const handleExamDateChange = async (e) => {
    const newExamDate = e.target.value;
    setExamDate(newExamDate);
    
    const { error } = await supabase
      .from('cases')
      .update({ exam_dt: newExamDate })
      .eq('case_id', caseId);

    if (error) {
      console.error('Error updating exam date:', error);
    }
  };

  const checkExistingFile = (fileName) => {
    // Check in all files, including deleted ones
    const existingFile = allCaseFiles.find(file => {
      const bucketFileName = file.bucket_url.split('/').pop();
      return (
        (file.filename === fileName || bucketFileName === fileName) && 
        !file.file_id.startsWith('temp_')
      );
    });
    
    return existingFile ? {
      file: existingFile,
      isSoftDeleted: existingFile.is_deleted
    } : null;
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!user) {
      return;
    }

    // Handle iCloud files
    const icloudFiles = acceptedFiles.filter(file => file.name.endsWith('.icloud'));
    if (icloudFiles.length > 0) {
      const icloudFileNames = icloudFiles.map(file => file.name).join(', ');
      setErrorMessage(`Couldn't upload ${icloudFileNames} because it is in iCloud. Download it to your device first (for example, to the desktop) then try uploading.`);
      setShowErrorNotification(true);
      return;
    }

    // For single file updates, check if file exists
    if (acceptedFiles.length === 1) {
      const existingFileInfo = checkExistingFile(acceptedFiles[0].name);
      
      if (existingFileInfo) {
        if (existingFileInfo.isSoftDeleted) {
          await updateFile(existingFileInfo.file, acceptedFiles[0], true);
          return;
        } else {
          setUpdateModal({
            isOpen: true,
            existingFile: existingFileInfo.file,
            newFile: acceptedFiles[0]
          });
          return;
        }
      }
    }

    // Validate files
    const supportedFiles = [];
    const unsupportedFiles = [];
    const oversizedFiles = [];

    for (const file of acceptedFiles) {
      const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;

      // Categorize file based on size and type
      if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(file);
      } else if (Object.values(SUPPORTED_FILE_TYPES).flat().includes(fileExtension)) {
        supportedFiles.push(file);
      } else {
        unsupportedFiles.push(file);
      }
    }

    // Show error messages for unsupported/oversized files
    if (unsupportedFiles.length > 0) {
      const unsupportedFileNames = unsupportedFiles.map(file => file.name).join(', ');
      setErrorMessage(`We couldn't upload: ${unsupportedFileNames}. We only support these filetypes at the moment: ${Object.values(SUPPORTED_FILE_TYPES).flat().join(', ')}. Try printing to PDF and uploading.`);
      setShowErrorNotification(true);
    }

    if (oversizedFiles.length > 0) {
      const oversizedFileNames = oversizedFiles.map(file => file.name).join(', ');
      setErrorMessage(`We couldn't upload: ${oversizedFileNames}. We currently only support up to 200MB per file upload. If you'd like to enable larger file uploads, please contact us at support@paribus.ai.`);
      setShowErrorNotification(true);
    }

    if (supportedFiles.length === 0) {
      return; // No supported files to process
    }

    // Create placeholder files for UI feedback
    const placeholderFiles = supportedFiles.map(file => ({
      file_id: `temp_${Date.now()}_${file.name}`,
      bucket_url: `cases/${caseId}/${file.name}`,
      category_id: 0,
      case_file_categories: { category_name: 'Processing' }
    }));

    setUploadedFiles(prevFiles => [...prevFiles, ...placeholderFiles]);
    setIsPolling(true);

    // Process uploads
    const uploads = supportedFiles.map(async (file, index) => {
      const filePath = `cases/${caseId}/${file.name}`;
      const bucketUrl = filePath;

      try {
        if (file.size >= MULTIPART_UPLOAD_THRESHOLD) {
          // Use multipart upload for large files
          const s3Client = await getS3Client();
          
          const upload = new S3Upload({
            client: s3Client,
            params: {
              Bucket: STORAGE_BUCKET,
              Key: filePath,
              Body: file,
            },
            queueSize: 4, // number of concurrent parts to upload
          });

          upload.on('httpUploadProgress', (progress) => {
            // You can add progress tracking here if desired
            console.log(`Upload progress: ${progress.loaded}/${progress.total}`);
          });

          await upload.done();
        } else {
          // Use regular upload for smaller files
          const { error } = await supabase.storage
            .from(STORAGE_BUCKET)
            .upload(filePath, file);

          if (error) throw error;
        }

        // Generate CloudFront URL
        const { data: signedData, error: signError } = await supabase.storage
          .from(STORAGE_BUCKET)
          .createSignedUrl(filePath, 3600);

        if (signError) throw signError;

        // Convert to CloudFront URL
        const cloudFrontUrl = signedData.signedUrl.replace(
          `${process.env.REACT_APP_SUPABASE_PROJECT_ID}.supabase.co/storage/v1/object/sign`,
          process.env.REACT_APP_CLOUDFRONT_DOMAIN
        );

        // Add or update file in case_files table
        const { data: fileData, error: dbError } = await supabase
          .from('case_files')
          .insert({ 
            case_id: caseId, 
            filename: file.name,
            bucket_url: bucketUrl,
            cloudfront_url: cloudFrontUrl,
            category_id: 0,
            processing_status: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (dbError) throw dbError;

        // Get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;

        // Notify backend about new/updated file
        const fileInfo = {
          file_id: fileData.file_id,
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type,
          uploaded_by: user.id
        };

        await notifyBackendOfUpload(fileInfo);

        // Invalidate cache if file was updated
        // await invalidateCache(bucketUrl);

        // Update placeholder with actual file data
        setUploadedFiles(prevFiles => prevFiles.map(f => 
          f.file_id === placeholderFiles[index].file_id ? fileData : f
        ));

        return fileData;

      } catch (error) {
        if (error.message?.includes('expired')) {
          // Handle token expiration
          try {
            const newClient = await refreshS3Client();
            // Retry the upload with new client
            // ... implement retry logic ...
          } catch (refreshError) {
            console.error('Failed to refresh S3 client:', refreshError);
            throw error;
          }
        }
        console.error('Caught error during upload:', error);
        // Remove placeholder if upload fails
        setUploadedFiles(prevFiles => prevFiles.filter(f => 
          f.file_id !== placeholderFiles[index].file_id
        ));
        return null;
      }
    });

    try {
      await Promise.all(uploads);
    } catch (error) {
      console.error('Error processing uploads:', error);
    }
  }, [caseId, user, allCaseFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    accept: SUPPORTED_FILE_TYPES
  });

  const groupFilesByCategory = () => {
    const groupedFiles = {};
    uploadedFiles.forEach(file => {
      const categoryId = file.category_id;
      const categoryName = file.processing_status === 0 ? 'Processing' : 
                          file.processing_status === 2 ? 'Failed' :
                          (file.case_file_categories?.category_name || 'Uncategorized');
      if (!groupedFiles[categoryId]) {
        groupedFiles[categoryId] = {
          categoryName: categoryName,
          files: []
        };
      }
      groupedFiles[categoryId].files.push(file);
    });
    return groupedFiles;
  };

  const downloadFile = async (bucketUrl) => {
    try {
      const bucketName = process.env.REACT_APP_SUPABASE_STORAGE_BUCKET;
      const { data: signedUrl } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(bucketUrl, 3600);

      if (!signedUrl?.signedUrl) {
        throw new Error('Failed to generate download URL');
      }

      // Fetch the file data as a blob
      const response = await fetch(signedUrl.signedUrl);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      
      // Create a blob URL and trigger download
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = bucketUrl.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const deleteFile = async (fileId) => {
    if (fileId.startsWith('temp_')) {
      // For temporary files, just remove from state
      setUploadedFiles(prevFiles => prevFiles.filter(file => file.file_id !== fileId));
    } else {
      const { data, error } = await supabase
        .from('case_files')
        .update({ is_deleted: true })
        .eq('file_id', fileId);

      if (error) {
        console.error('Error marking file as deleted:', error);
        return;
      }
      console.log('File marked as deleted:', data);

      // Update state
      setUploadedFiles(prevFiles => prevFiles.filter(file => file.file_id !== fileId));
    }
  };

  const fetchFileStatus = useCallback(async (fileId) => {
    // Skip temporary files
    if (fileId.startsWith('temp_')) {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('case_files')
        .select('file_id, category_id, processing_status, case_file_categories(category_name)')
        .eq('file_id', fileId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching file status:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    let intervalId;

    const pollFileStatus = async () => {
      // Check for files that are still processing
      const processingFiles = uploadedFiles.filter(file => 
        file.processing_status === 0 && !file.file_id.startsWith('temp_')
      );

      if (processingFiles.length === 0) {
        setIsPolling(false);
        return;
      }

      let updatedFiles = false;
      for (const file of processingFiles) {
        const status = await fetchFileStatus(file.file_id);
        if (status && status.processing_status !== 0) {
          setUploadedFiles(prevFiles =>
            prevFiles.map(f => f.file_id === file.file_id ? { ...f, ...status } : f)
          );
          updatedFiles = true;
        }
      }

      if (!updatedFiles) {
        console.log('No updates detected');
      }
    };

    if (isPolling) {
      intervalId = setInterval(pollFileStatus, 2000); // Poll every 2 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [uploadedFiles, fetchFileStatus, isPolling]);

  const updateFile = async (existingFile, newFile, isSoftDeleted = false) => {
    // Close modal if it was open
    setUpdateModal({ isOpen: false, existingFile: null, newFile: null });
    
    try {
      const filePath = existingFile.bucket_url;

      // For soft-deleted files, we need to add them back to uploadedFiles
      if (isSoftDeleted) {
        setUploadedFiles(prevFiles => [
          ...prevFiles,
          {
            ...existingFile,
            processing_status: 0,
            is_deleted: false,
            case_file_categories: { category_name: 'Processing' }
          }
        ]);
      } else {
        // For existing files, update their status
        setUploadedFiles(prevFiles => prevFiles.map(file => 
          file.file_id === existingFile.file_id 
            ? {
                ...file,
                processing_status: 0,
                is_deleted: false,
                case_file_categories: { category_name: 'Processing' }
              }
            : file
        ));
      }
      
      // Start polling for updates
      setIsPolling(true);
      
      // Upload new file to same path
      const { error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .update(filePath, newFile);

      if (uploadError) throw uploadError;

      // Generate new CloudFront URL
      const { data: signedData, error: signError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(filePath, 3600);

      if (signError) throw signError;

      // Convert to CloudFront URL
      const cloudFrontUrl = signedData.signedUrl.replace(
        `${process.env.REACT_APP_SUPABASE_PROJECT_ID}.supabase.co/storage/v1/object/sign`,
        process.env.REACT_APP_CLOUDFRONT_DOMAIN
      );

      // Update database record with processing status
      const { data: fileData, error: updateError } = await supabase
        .from('case_files')
        .update({ 
          cloudfront_url: cloudFrontUrl,
          processing_status: 0,
          category_id: 0,
          is_deleted: false,
          updated_at: new Date().toISOString()
        })
        .eq('file_id', existingFile.file_id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      // Notify backend about updated file
      const fileInfo = {
        file_id: fileData.file_id,
        file_name: newFile.name,
        file_size: newFile.size,
        mime_type: newFile.type,
        uploaded_by: user.id
      };

      await notifyBackendOfUpload(fileInfo);

      // Try to invalidate cache using CloudFront URL path
      try {
        const cloudfrontPath = new URL(cloudFrontUrl).pathname;
        await invalidateCache(cloudfrontPath);
      } catch (cacheError) {
        console.warn('Cache invalidation failed, but file was updated:', cacheError);
      }

    } catch (error) {
      console.error('Error in updateFile:', error);
      setErrorMessage('Failed to update file. Please try again.');
      setShowErrorNotification(true);
      
      // Revert the processing status in case of error
      setUploadedFiles(prevFiles => prevFiles.map(file => 
        file.file_id === existingFile.file_id 
          ? existingFile // Revert to original state
          : file
      ));
      setIsPolling(false);
    }
  };

  const handleRequestReview = async () => {
    if (uploadedFiles.length === 0) {
      setErrorMessage('Please upload at least one file before requesting a review.');
      setShowErrorNotification(true);
      return;
    }

    if (!firmContext?.firmId) {
      setErrorMessage('No firm context found. Please try logging out and back in.');
      setShowErrorNotification(true);
      return;
    }

    // Check for physician selection
    if (!isProfessional && !selectedProfessional?.user_id) {
      setShowPhysicianError(true);
      return;
    }

    // Reset error state if we're proceeding
    setShowPhysicianError(false);

    // Start loading state
    setIsSubmitting(true);

    try {
      // Run the professional update and case update in parallel
      const updatePromises = [];
      
      if (isProfessional) {
        updatePromises.push(
          api.put(`/cases/${caseId}/physician`, {
            user_id: user.id
          })
        );
      } else if (!selectedProfessional?.user_id) {
        setErrorMessage('Please select a physician before requesting a review.');
        setShowErrorNotification(true);
        return;
      } else {
        updatePromises.push(
          api.put(`/cases/${caseId}/physician`, {
            user_id: selectedProfessional.user_id
          })
        );
      }

      // Add case update to parallel promises
      updatePromises.push(
        supabase
          .from('cases')
          .update({ 
            firm_id: firmContext.firmId,
            exam_dt: examDate,
            status_id: 1
          })
          .eq('case_id', caseId)
      );

      // Wait for all updates to complete
      await Promise.all(updatePromises);

      // Make the med_chron request
      const response = await api.post('/med_chron/request', {
        case_id: caseId,
        urgent: isRushRequest
      });

      if (response.data.status === 'success') {
        setCaseStatus(1);
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
          navigate('/cases');
        }, 2000);
      } else {
        throw new Error('Failed to submit request');
      }
    } catch (error) {
      if (error.message === 'Session expired') {
        return;
      }
      console.error('Error requesting review:', error);
      setErrorMessage('Failed to request review. Please try again.');
      setShowErrorNotification(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameEdit = () => {
    setIsEditingName(true);
  };

  const handleNameSave = async () => {
    setIsEditingName(false);
    await handleCaseNameBlur({ target: { value: caseName } });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  const notifyBackendOfUpload = async (fileInfo) => {
    try {
      const response = await api.post('/file-uploaded/', fileInfo);
    } catch (error) {
      if (error.message === 'Session expired') {
        return; // API service will handle redirect
      }
      console.error('Error notifying backend:', error);
      throw error;
    }
  };

  const updateFileMapping = async (caseId) => {
    try {
      const { data, error } = await supabase
        .from('case_files')
        .select('file_id, bucket_url')
        .eq('case_id', caseId);

      if (error) {
        console.error('Error fetching file mapping:', error);
        return;
      }

      const newMapping = data.reduce((acc, file) => {
        const fileName = file.bucket_url ? file.bucket_url.split('/').pop() : 'Unknown file';
        acc[file.file_id] = fileName;
        return acc;
      }, {});

      setFileMapping(newMapping);
    } catch (error) {
      console.error('Error updating file mapping:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      // Check if case is in AI Review and user is not super admin
      if (caseStatus === 1 && !hasPermission('super_admin')) {
        toast.error('Only super admins can move cases out of AI Review');
        return;
      }

      // Additional permission checks for specific statuses
      if (newStatus === 4 && !hasPermission('case_actions', 'finalize')) {
        toast.error('You do not have permission to finalize cases');
        return;
      }

      if (newStatus === 3 && !hasPermission('case_actions', 'mark_reviewed')) {
        toast.error('You do not have permission to mark cases as reviewed');
        return;
      }

      await api.put(`/cases/${caseId}/status`, { status_id: newStatus });
      setCaseStatus(newStatus);
    } catch (error) {
      console.error('Error updating case status:', error);
      toast.error('Failed to update case status');
    }
  };

  const downloadAllFiles = async () => {
    if (uploadedFiles.length === 0) {
      toast.error('No files to download');
      return;
    }

    try {
      const zip = new JSZip();
      const bucketName = process.env.REACT_APP_SUPABASE_STORAGE_BUCKET;

      // Show loading toast
      const loadingToast = toast.loading('Preparing files for download...');

      // Download all files and add them to the zip
      const downloadPromises = uploadedFiles.map(async (file) => {
        try {
          const { data: signedUrl } = await supabase.storage
            .from(bucketName)
            .createSignedUrl(file.bucket_url, 3600);

          if (!signedUrl?.signedUrl) {
            throw new Error('Failed to generate download URL');
          }

          const response = await fetch(signedUrl.signedUrl);
          if (!response.ok) {
            throw new Error('Failed to download file');
          }

          const blob = await response.blob();
          const fileName = file.bucket_url.split('/').pop();
          zip.file(fileName, blob);

        } catch (error) {
          console.error(`Error downloading file ${file.bucket_url}:`, error);
          throw error;
        }
      });

      await Promise.all(downloadPromises);

      // Generate the zip file
      const content = await zip.generateAsync({ type: 'blob' });
      
      // Create download link
      const downloadUrl = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${caseName || 'case'}_files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      // Close loading toast and show success
      toast.dismiss(loadingToast);
      toast.success('Files downloaded successfully');

    } catch (error) {
      console.error('Error downloading files:', error);
      toast.error('Failed to download files');
    }
  };

  return (
    <div className="flex-1 p-8 bg-gray-100 relative flex">
      <div className={`w-full max-w-4xl mx-auto ${showRightPanel ? 'mr-[500px]' : ''}`}>
        {showErrorNotification && (
          <div className="absolute top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md shadow-md" role="alert">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline"> {errorMessage}</span>
            <span className="absolute top-0 bottom-0 right-0 px-4 py-3">
              <X
                className="fill-current h-6 w-6 text-red-500 cursor-pointer"
                onClick={() => setShowErrorNotification(false)}
              />
            </span>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="flex items-center">
                {isEditingName ? (
                  <input
                    type="text"
                    value={caseName}
                    onChange={(e) => setCaseName(e.target.value)}
                    onBlur={handleNameSave}
                    onKeyDown={handleKeyPress}
                    className="text-2xl font-bold border-b-2 border-blue-500 focus:outline-none bg-transparent"
                    autoFocus
                  />
                ) : (
                  <div className="flex items-center">
                    <h2 className="text-2xl font-bold">
                      {caseName || "New Case"}
                    </h2>
                    <button 
                      className="ml-2 text-gray-400 hover:text-gray-600"
                      onClick={handleNameEdit}
                    >
                      <Pencil size={16} />
                    </button>
                  </div>
                )}
              </div>
              <StatusBadge 
                status={caseStatus} 
                onStatusChange={handleStatusChange} 
                startTime={caseStatus === 1 ? new Date().toISOString() : null}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div 
            {...getRootProps()} 
            className="border-3 border-dashed border-indigo-400 rounded-lg p-8 text-center cursor-pointer mb-6 bg-gradient-to-br from-indigo-50 to-purple-50 shadow-lg hover:shadow-xl transition duration-300"
          >
            <input {...getInputProps()} />
            <div className="flex items-center justify-center mb-4">
              <Upload className="text-indigo-500 mr-2" size={36} />
              <FileUp className="text-purple-500 ml-2" size={36} />
            </div>
            <p className="text-xl font-bold text-indigo-700 mb-2">
              {isDragActive ? "Drop the files here" : "Upload case files"}
            </p>
            <p className="text-sm text-indigo-600">
              Drag and drop files here or click to select
            </p>
          </div>
          
          {uploadedFiles.length > 0 && (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Uploaded files</h2>
                <button
                  onClick={downloadAllFiles}
                  className="flex items-center justify-center px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition duration-200"
                >
                  <Download size={20} className="mr-2" />
                  Download All
                </button>
              </div>
              {renderFiles()}
            </>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6 max-w-[600px] mx-auto">
          <h3 className="text-lg font-semibold mb-4 text-center">Request Review</h3>
          <div className="mb-6">
            <div className="flex flex-col gap-4">
              <div className={`text-sm ${showPhysicianError ? 'text-red-500' : 'text-gray-500'}`}>
                Review for:
                {isProfessional ? (
                  <span className="ml-2">
                    {userSettings?.name || "Unknown"}
                  </span>
                ) : loadingPhysicians ? (
                  <span className="ml-2 text-gray-400">Loading physicians...</span>
                ) : (
                  <select
                    className={`ml-2 border-b ${showPhysicianError ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'} focus:outline-none bg-transparent`}
                    value={selectedProfessional?.user_id || ''}
                    onChange={async (e) => {
                      setShowPhysicianError(false);
                      const newProId = e.target.value;
                      const selectedPro = physicians.find(p => p.user_id === newProId);
                      setSelectedProfessional(selectedPro);
                    }}
                  >
                    <option value="">Select physician</option>
                    {physicians.map(physician => (
                      <option key={physician.user_id} value={physician.user_id}>
                        {physician.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex items-center gap-6">
                <div>
                  <label htmlFor="examDate" className="block text-sm font-medium text-gray-700 mb-1">
                    Exam Date
                  </label>
                  <input 
                    id="examDate"
                    type="date" 
                    value={examDate}
                    onChange={handleExamDateChange}
                    className="w-48 p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                
                <div className="flex items-center mt-6">
                  <label className={`
                    flex items-center px-4 py-2 rounded-full cursor-pointer transition-colors duration-200
                    ${isRushRequest 
                      ? 'bg-red-50 hover:bg-red-100' 
                      : 'bg-gray-100 hover:bg-gray-200'
                    }
                  `}>
                    <input
                      type="checkbox"
                      checked={isRushRequest}
                      onChange={(e) => setIsRushRequest(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`
                      w-5 h-5 rounded border-2 mr-2 flex items-center justify-center
                      ${isRushRequest 
                        ? 'border-red-500 bg-red-500' 
                        : 'border-gray-400 bg-white'
                      }
                    `}>
                      {isRushRequest && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-sm font-medium ${
                      isRushRequest ? 'text-red-700' : 'text-gray-700'
                    }`}>
                      Rush Request
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <button 
            className={`w-full py-2 px-4 rounded-lg mt-4 flex items-center justify-center ${
              caseStatus === 0 && !isSubmitting
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={handleRequestReview}
            disabled={caseStatus !== 0 || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting Request...
              </>
            ) : (
              'Request Records Review'
            )}
          </button>
        </div>
      </div>

      {renderRightPanel()}

      <Modal
        open={updateModal.isOpen}
        onClose={() => setUpdateModal({ isOpen: false, existingFile: null, newFile: null })}
        className="flex items-center justify-center"
      >
        <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
          <h3 className="text-lg font-bold mb-4">Update File</h3>
          <p className="mb-4">
            "{updateModal.existingFile?.bucket_url.split('/').pop()}" already exists. 
            Would you like to replace it?
          </p>
          <div className="flex justify-end gap-4">
            <button
              onClick={() => setUpdateModal({ isOpen: false, existingFile: null, newFile: null })}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const { existingFile, newFile } = updateModal;
                updateFile(existingFile, newFile);
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Replace
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        className="flex items-center justify-center"
      >
        <div className="bg-white rounded-lg p-6 max-w-md mx-auto text-center">
          <h3 className="text-lg font-semibold mb-2">Processing Request</h3>
          <p className="text-gray-600">
            Submitting your review request. You'll be redirected to the cases page shortly.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default FileManagement;
